import React, { useMemo, useState } from 'react';

import { GroupedBars, Legend, LineChart } from './charts';
import { BusDiagram, DiagramKey } from './diagrams';
import { SeverityPill } from './views-site';
import { At, BusBadge, BusTable, Caption, Foot, fmt, Grid, Kpi, KpiRow, Note, Panel, Pill, plural, Readout, Row, Segmented, Select, Table } from './parts';
import {
    BUSES, BUS_COLOR, clock, fastCapture, hourlyEnergy, inScope, liveHistory, protectionHistory,
    scopedConditions, SYSTEM, thermalCoverage, THRESHOLDS, timeToCritical
} from './model';

const buses = scope => BUSES.filter(bus => inScope(scope, bus));

/* The hottest reading in scope that is actually current: a stale channel is
   left out of this rather than quietly counted as whatever it last said. */
const hottest = (dc, scope) => buses(scope)
    .flatMap(bus => dc.buses[bus].feeders)
    .filter(feeder => !feeder.stale)
    .reduce((top, feeder) => (feeder.temp > top.temp ? feeder : top));

const StatusFor = ({ feeder, condition }) => {
    if (feeder.stale) return <Pill>Temp unknown</Pill>;
    return condition ? <SeverityPill severity={condition.severity} /> : <Pill tone="ok">Normal</Pill>;
};

const tempTag = temp => {
    if (temp >= 75) return <Pill tone="crit">Critical</Pill>;
    return temp >= 60 ? <Pill tone="warn">Advisory</Pill> : <Pill tone="ok">Normal</Pill>;
};

/* ── UPS DC overview ────────────────────────────────────────── */

const METRICS = {
    power: { label: 'DC power', key: 'kw', scale: 1 / 1000, unit: 'MW', places: 3, title: 'Power demand' },
    voltage: { label: 'Bus voltage', key: 'volts', scale: 1, unit: 'VDC', places: 1, title: 'Bus voltage trend' },
    current: { label: 'Bus current', key: 'amps', scale: 1, unit: 'A', places: 0, title: 'Bus current trend' }
};

const RANGE_SAMPLES = { 1: 13, 6: 73, 24: 288 };

export function Overview({ sim }) {
    const { dc, scope, openFeeder, setView } = sim;
    const [range, setRange] = useState(6);
    const [metricId, setMetricId] = useState('power');
    const [query, setQuery] = useState('');
    const [filter, setFilter] = useState('all');

    const metric = METRICS[metricId];
    const single = scope !== 'both';
    const data = single ? dc.buses[scope] : null;
    const hot = hottest(dc, scope);
    const conditions = scopedConditions(dc, scope);

    const trend = buses(scope).map(bus => ({
        id: bus,
        label: `Bus ${bus}`,
        color: BUS_COLOR[bus],
        area: buses(scope).length === 1,
        points: liveHistory(bus, dc, RANGE_SAMPLES[range]).map(p => [p.t, p[metric.key] * metric.scale])
    }));

    const sparkLists = buses(scope).map(bus => liveHistory(bus, dc, 36));
    const spark = {
        power: sparkLists[0].map((_, i) => sparkLists.reduce((total, points) => total + points[i].kw, 0)),
        volts: sparkLists[0].map(p => p.volts)
    };

    const inBus = buses(scope).flatMap(bus => dc.buses[bus].feeders);
    const order = [...inBus].sort((a, b) => a.index - b.index || a.bus.localeCompare(b.bus));
    const topLoads = [...inBus].sort((a, b) => b.loading - a.loading).slice(0, 4).map(f => f.id);
    const flagged = dc.conditions.map(c => c.feeder);

    const shown = order.filter(feeder => {
        const needle = query.trim().toLowerCase();
        const matches = !needle || `${feeder.id} ${feeder.joint} ${feeder.location} ${feeder.inverter}`.toLowerCase().includes(needle);

        if (!matches) return false;
        if (filter === 'attention') return flagged.includes(feeder.id);
        if (filter === 'load') return topLoads.includes(feeder.id);
        return true;
    });

    const xTicks = points => {
        const list = points[0].points;
        return [0, 1 / 3, 2 / 3, 1].map(f => list[Math.round((list.length - 1) * f)][0]);
    };

    return (
        <>
            <KpiRow>
                <Kpi
                    label={single ? `Bus ${scope} power` : 'Total DC bus power'}
                    value={fmt((single ? data.kw : dc.totalKw) / 1000, 3)}
                    unit="MW"
                    note={single ? `Source ${scope} output` : 'Across A + B · positive toward load'}
                    spark={spark.power}
                />
                <Kpi
                    label={single ? 'Bus voltage' : 'Bus A voltage'}
                    value={fmt(single ? data.volts : dc.buses.A.volts)}
                    unit="VDC"
                    note={single ? 'Pole-to-pole' : `Bus B ${fmt(dc.buses.B.volts)} V`}
                    tag={<Pill tone={(single ? data.volts : dc.buses.A.volts) >= 790 ? 'ok' : 'warn'}>{(single ? data.volts : dc.buses.A.volts) >= 790 ? 'In range' : 'Off nominal'}</Pill>}
                    spark={spark.volts}
                />
                <Kpi
                    label={single ? `Bus ${scope} loading` : 'Highest bus loading'}
                    value={fmt(single ? data.loading : Math.max(dc.buses.A.loading, dc.buses.B.loading))}
                    unit="%"
                    note={single ? `${fmt(data.amps, 0)} A of 4,000 A` : `A ${fmt(dc.buses.A.amps, 0)} A · B ${fmt(dc.buses.B.amps, 0)} A · 4,000 A per bus`}
                />
                <Kpi
                    label="Hottest valid connection"
                    value={fmt(hot.temp)}
                    unit="°C"
                    note={`${hot.joint} · +${fmt(hot.rise)} K rise`}
                    tag={tempTag(hot.temp)}
                />
            </KpiRow>

            <Grid cols="minmax(0, 1.5fr) minmax(0, 1fr)">
                <Panel
                    title="UPS DC distribution"
                    sub="Independent DC links · 6 inverter sections per path"
                    badge={<Legend items={[{ label: 'Bus A', color: BUS_COLOR.A }, { label: 'Bus B', color: BUS_COLOR.B }]} />}
                >
                    <BusDiagram dc={dc} scope={scope} onOpen={openFeeder} />
                    <DiagramKey items={[
                        { mark: 'breaker', label: 'Breaker closed' },
                        { mark: 'breaker-open', label: 'Breaker open' },
                        { mark: 'hot', label: 'Thermal advisory' },
                        { mark: 'flow', label: 'Power flow' }
                    ]} />
                    <Caption>Click a tap-off to inspect · Functional one-line · Each route represents a +/− pair · No bus tie · Separate A / B inverters</Caption>
                </Panel>

                <Panel
                    title={metric.title}
                    sub="Historical trend · Eastern time"
                    actions={(
                        <Segmented
                            label="Trend range"
                            value={range}
                            onChange={setRange}
                            options={[{ value: 1, label: '1h' }, { value: 6, label: '6h' }, { value: 24, label: '24h' }]}
                        />
                    )}
                >
                    <div className="dash-controls dash-controls--spread">
                        {trend.length > 1
                            ? <Legend items={trend.map(s => ({ label: s.label, color: s.color }))} />
                            : <span className="dash-axis">{trend[0].label}</span>}
                        <Select
                            label="Trend"
                            value={metricId}
                            onChange={setMetricId}
                            options={Object.entries(METRICS).map(([value, item]) => ({ value, label: item.label }))}
                        />
                    </div>

                    <LineChart
                        series={trend}
                        height={270}
                        label={`${metric.label} over the last ${range} hours`}
                        xTicks={xTicks(trend)}
                        formatX={clock}
                        formatY={value => value.toLocaleString('en-US')}
                        formatTip={value => `${fmt(value, metric.places)} ${metric.unit}`}
                        tipTitle={value => `${clock(value)} ET`}
                        yLabel={metric.unit}
                    />

                    <Caption>
                        15-minute demand {fmt(buses(scope).reduce((t, b) => t + dc.buses[b].demandKw, 0) / 1000, 3)} MW · 24-hour import {fmt(buses(scope).reduce((t, b) => t + dc.buses[b].importKwh, 0) / 1000, 2)} MWh
                    </Caption>
                </Panel>
            </Grid>

            <Grid cols="minmax(0, 1.6fr) minmax(0, 1fr)">
                <Panel title="Tap-off feeders" sub="DC feeds to inverter sections">
                    <div className="dash-controls">
                        <label className="dash-search">
                            <span className="dash-sr">Find feeder</span>
                            <input type="search" placeholder="Find feeder…" value={query} onChange={event => setQuery(event.target.value)} />
                        </label>
                        <Select
                            label="Show"
                            value={filter}
                            onChange={setFilter}
                            options={[
                                { value: 'all', label: 'All feeders' },
                                { value: 'attention', label: 'Needs attention' },
                                { value: 'load', label: 'Highest load' }
                            ]}
                        />
                    </div>

                    <Table>
                        <thead>
                            <tr>
                                <th scope="col">Feeder</th>
                                <th scope="col" className="is-num">Voltage</th>
                                <th scope="col" className="is-num">Current</th>
                                <th scope="col" className="is-num">Power</th>
                                <th scope="col">Loading</th>
                                <th scope="col" className="is-num">Joint temp.</th>
                            </tr>
                        </thead>
                        <tbody>
                            {shown.map(feeder => {
                                const condition = dc.conditions.find(c => c.feeder === feeder.id && c.kind === 'thermal');

                                return (
                                    <tr key={feeder.id} className={flagged.includes(feeder.id) ? 'is-flagged' : undefined}>
                                        <th scope="row">
                                            <button type="button" className="dash-tablelink" onClick={() => openFeeder(feeder.id)}>
                                                <BusBadge bus={feeder.bus} /> {feeder.id}
                                            </button>
                                            <span className="dash-table__inline">Section {String(feeder.index + 1).padStart(2, '0')}</span>
                                        </th>
                                        <td className="is-num">{fmt(feeder.volts)} V</td>
                                        <td className="is-num">{fmt(feeder.amps, 0)} A</td>
                                        <td className="is-num">{fmt(feeder.kw)} kW</td>
                                        <td>
                                            <span className="dash-load">
                                                <span><i style={{ width: `${feeder.loading}%`, background: BUS_COLOR[feeder.bus] }} /></span>
                                                {fmt(feeder.loading)}%
                                            </span>
                                        </td>
                                        <td className="is-num">
                                            {feeder.stale && <Pill>Stale</Pill>}
                                            {!feeder.stale && condition && <Pill tone={condition.severity === 'critical' ? 'crit' : 'warn'}>{fmt(feeder.temp)} °C</Pill>}
                                            {!feeder.stale && !condition && `${fmt(feeder.temp)} °C`}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>

                    {shown.length === 0 && <Note>No feeder matches that. Clear the search or show all feeders.</Note>}

                    <div className="dash-panel__after dash-panel__after--spread">
                        <Caption>{shown.length} of {inBus.length} feeders shown · 800 A configured continuous limit</Caption>
                        <button type="button" className="dash-link" onClick={() => setView('library')}>All measurements →</button>
                    </div>
                </Panel>

                <Panel title="Protection & resilience" sub="Electrical health at a glance" badge={<Pill tone="ok">Monitoring</Pill>}>
                    <Readout items={[
                        { label: 'Minimum insulation', value: fmt(Math.min(...buses(scope).map(b => dc.buses[b].insulation)), 2), unit: 'MΩ', note: 'IMD measurement valid' },
                        { label: 'Maximum residual · AC+DC', value: fmt(Math.max(...buses(scope).map(b => dc.buses[b].residual))), unit: 'mA', note: 'RMS · DC–1 kHz band' }
                    ]} />

                    <div className="dash-meter">
                        <span className="dash-meter__head">
                            <span>Surviving-path utilization</span>
                            <strong>{fmt(dc.survivingUse)}%</strong>
                        </span>
                        <span className="dash-meter__track"><i style={{ width: `${dc.survivingUse}%` }} /></span>
                        <span className="dash-meter__foot">
                            <span>Facility 2N model · after A or B loss</span>
                            <span>{fmt(dc.headroomKw / 1000, 2)} MW margin</span>
                        </span>
                    </div>

                    <div className="dash-subhead">
                        <h3>Active advisories ({conditions.length})</h3>
                        <button type="button" className="dash-link" onClick={() => setView('protection')}>View all →</button>
                    </div>

                    <ul className="dash-alerts">
                        {conditions.map(condition => (
                            <li key={condition.id}>
                                <button
                                    type="button"
                                    className={`is-${condition.severity}`}
                                    onClick={() => (condition.feeder ? openFeeder(condition.feeder) : setView('protection'))}
                                >
                                    <strong>{condition.asset} · {condition.short}</strong>
                                    <span>{condition.summary}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </Panel>
            </Grid>

        </>
    );
}

/* ── DC electrical ──────────────────────────────────────────── */

export function Electrical({ sim }) {
    const { dc, scope } = sim;
    const A = dc.buses.A;
    const B = dc.buses.B;
    const list = buses(scope).map(bus => dc.buses[bus]);

    const totalKw = list.reduce((t, b) => t + b.kw, 0);
    const feederKw = list.reduce((t, b) => t + b.feederKw, 0);
    const demand = list.reduce((t, b) => t + b.demandKw, 0);
    const loss = totalKw - feederKw;
    const scopeLabel = scope === 'both' ? 'A + B buses' : `Bus ${scope}`;

    const signed = (value, places = 1) => `${value >= 0 ? '+' : ''}${fmt(value, places)}`;

    return (
        <>
            <KpiRow>
                <Kpi label="DC bus input power" value={fmt(totalKw / 1000, 2)} unit="MW" note={`${scopeLabel} · positive import`} />
                <Kpi label="Highest bus loading" value={fmt(Math.max(...list.map(b => b.loading)))} unit="%" note="Of 4,000 A per path" />
                <Kpi label="Highest voltage ripple" value={fmt(Math.max(...list.map(b => b.ripple.voltsPct)), 3)} unit="%" note="RMS ripple / DC mean" />
                <Kpi label="15-minute demand" value={fmt(demand / 1000, 2)} unit="MW" note={`${scopeLabel} · rolling average`} />
            </KpiRow>

            <Grid cols="minmax(0, 1.4fr) minmax(0, 1fr)">
                <Panel title="DC measurements" sub="Per-bus quantities · independent A and B paths" badge={<Pill tone="info">DC · 2 wire</Pill>}>
                    <BusTable scope={scope} rows={[
                        { label: 'Pole-to-pole voltage', note: 'DC mean, + conductor to − conductor', a: fmt(A.volts), b: fmt(B.volts), unit: 'V DC' },
                        { label: 'Positive pole to PE', note: 'Signed voltage to protective earth', a: signed(A.positivePe), b: signed(B.positivePe), unit: 'V DC' },
                        { label: 'Negative pole to PE', note: 'Signed voltage to protective earth', a: signed(A.negativePe), b: signed(B.negativePe), unit: 'V DC' },
                        { label: 'Deviation from nominal', note: '(Measured voltage − 800 V) / 800 V', a: signed(A.deviation, 2), b: signed(B.deviation, 2), unit: '%' },
                        { label: 'Lowest 5-min mean voltage', note: 'Normal-operation minimum · 24-hour historian', a: <At value={fmt(A.minVolts.volts)} time={`${clock(A.minVolts.t)} ET`} />, b: <At value={fmt(B.minVolts.volts)} time={`${clock(B.minVolts.t)} ET`} />, unit: 'V DC' },
                        { label: 'Highest 5-min mean voltage', note: 'Normal-operation maximum · 24-hour historian', a: <At value={fmt(A.maxVolts.volts)} time={`${clock(A.maxVolts.t)} ET`} />, b: <At value={fmt(B.maxVolts.volts)} time={`${clock(B.maxVolts.t)} ET`} />, unit: 'V DC' },
                        { label: 'Mean bus current', note: 'One DC path, positive toward load', a: fmt(A.amps), b: fmt(B.amps), unit: 'A DC' },
                        { label: 'Total RMS current', note: '√(I DC² + I ripple RMS²), derived', a: fmt(A.totalRmsA), b: fmt(B.totalRmsA), unit: 'A RMS' },
                        { label: 'Peak 5-min mean current', note: '24-hour historian; not a fast event peak', a: <At value={fmt(A.peakAmps.amps)} time={`${clock(A.peakAmps.t)} ET`} />, b: <At value={fmt(B.peakAmps.amps)} time={`${clock(B.peakAmps.t)} ET`} />, unit: 'A DC' },
                        { label: 'Parallel-conductor sharing', note: 'Needs individual conductor sensors', a: <Pill>Not instrumented</Pill>, b: <Pill>Not instrumented</Pill>, unit: 'A / %' },
                        { label: 'DC power', note: 'Steady-state, V DC × I DC', a: fmt(A.kw), b: fmt(B.kw), unit: 'kW' },
                        { label: 'Bus loading', note: '4,000 A continuous limit assumed', a: fmt(A.loading), b: fmt(B.loading), unit: '%' },
                        { label: '15-minute demand', note: 'Rolling average · simulated historian', a: fmt(A.demandKw), b: fmt(B.demandKw), unit: 'kW' },
                        { label: 'Peak 15-minute demand', note: 'Maximum in the 24-hour record', a: fmt(A.peakDemandKw), b: fmt(B.peakDemandKw), unit: 'kW' }
                    ]} />
                    <Foot>PE is a voltage reference, not a load return. On a floating two-wire system, pole-to-earth voltages can shift while pole-to-pole voltage stays normal. Sampled 5-minute extrema exclude fast transients: the capture below records a 772.4 V minimum that they never see.</Foot>
                </Panel>

                <div className="dash-stack">
                    <Panel title="Ripple & power quality" sub="AC component on top of the DC bus">
                        <BusTable scope={scope} rows={[
                            { label: 'Voltage ripple RMS', note: 'AC component after removing DC mean', a: fmt(A.ripple.volts, 2), b: fmt(B.ripple.volts, 2), unit: 'V RMS' },
                            { label: 'Voltage ripple peak-to-peak', note: 'Max − min of filtered capture', a: fmt(A.ripple.peakToPeak, 2), b: fmt(B.ripple.peakToPeak, 2), unit: 'V p-p' },
                            { label: 'Voltage ripple factor', note: '100 × ripple RMS / |DC mean|', a: fmt(A.ripple.voltsPct, 3), b: fmt(B.ripple.voltsPct, 3), unit: '%' },
                            { label: 'Current ripple RMS', note: 'AC component · DC-capable sensor', a: fmt(A.ripple.amps), b: fmt(B.ripple.amps), unit: 'A RMS' },
                            { label: 'Current ripple factor', note: '100 × ripple RMS / |DC mean|', a: fmt(A.ripple.ampsPct, 2), b: fmt(B.ripple.ampsPct, 2), unit: '%' }
                        ]} />
                        <Readout items={[
                            { label: 'Design bandwidth', value: '10 Hz–20 kHz' },
                            { label: 'Capture window', value: '100', unit: 'ms' },
                            { label: 'Acquisition target', value: '100', unit: 'kS/s' },
                            { label: 'Result aggregate', value: '1', unit: 's' }
                        ]} />
                        <Foot>The acquisition specification, with anti-alias filtering. Hardware sets the effective bandwidth and accuracy, and the dashboard refresh is separate from waveform sampling.</Foot>
                    </Panel>

                    <Panel title="Distribution loss & efficiency" sub={`${scopeLabel} · bus input to outgoing feeder meters`} badge={<Pill>Derived</Pill>}>
                        <Readout items={[
                            { label: 'Bus input', value: fmt(totalKw, 2), unit: 'kW' },
                            { label: 'Feeder output', value: fmt(feederKw, 2), unit: 'kW' },
                            { label: 'Distribution loss', value: fmt(loss, 2), unit: 'kW' },
                            { label: 'Efficiency', value: fmt((feederKw / totalKw) * 100, 2), unit: '%' }
                        ]} />
                        <Foot>Loss is bus-input power minus summed feeder-output power. This boundary excludes rectifier conversion losses, and small real losses need synchronized meters with suitable uncertainty.</Foot>
                    </Panel>
                </div>
            </Grid>

            <Grid>
                <Panel title="Fast event capture" sub="Triggered local recorder · separate from dashboard polling">
                    <Readout items={[
                        { label: 'Recorded minimum', value: fmt(fastCapture.minimum), unit: 'V' },
                        { label: 'Duration below 780 V', value: fmt(fastCapture.below780), unit: 'ms' },
                        { label: 'Capture location', value: fastCapture.location },
                        { label: 'Sample event ID', value: fastCapture.id }
                    ]} />
                    <LineChart
                        series={[{ id: 'bus', label: 'Bus A voltage', color: BUS_COLOR.A, area: false, points: fastCapture.points }]}
                        height={220}
                        domain={[760, 810]}
                        shade={[0, fastCapture.below780]}
                        label="Bus A voltage dipping to 772.4 volts for 12.4 milliseconds"
                        xTicks={[-10, 0, 10, 20]}
                        formatX={value => `${value} ms`}
                        formatY={value => `${value} V`}
                        formatTip={value => `${value.toFixed(1)} V`}
                        tipTitle={value => `${value.toFixed(1)} ms from trigger`}
                    />
                    <Caption>Relative trigger time · Shaded band is the 12.4 ms below 780 V · Recovered, record retained</Caption>
                </Panel>

                <Panel title="Measurement context" sub="Definitions that travel with the data">
                    <Row label="Power-flow sign" note="Positive means source toward DC load"><Pill tone="info">Import +</Pill></Row>
                    <Row label="Actual active power" note="Mean of simultaneous instantaneous v(t) × i(t)"><code>mean(v · i)</code></Row>
                    <Row label="Displayed DC power" note="Steady-state averages; ripple power correlation omitted"><code>V DC × I DC</code></Row>
                    <Row label="Event record requirements" note="Trigger, extrema, duration, dV/dt, dI/dt, pre/post waveform" />
                    <Row label="Quality and provenance" note="Sample time, validity, clock sync and measurement window">
                        <button type="button" className="dash-link" onClick={() => sim.setView('health')}>Sensor health →</button>
                    </Row>
                    <Foot>DC ripple percentages are not AC THD. Frequency, power factor and reactive power belong to the AC meters. Displayed DC power is a steady-state V × I; real DC power uses synchronized voltage and current samples, and fast events and ripple need a local recorder with the stated bandwidth.</Foot>
                </Panel>
            </Grid>

        </>
    );
}

/* ── Thermal health ─────────────────────────────────────────── */

export function Thermal({ sim }) {
    const { dc, scope, openFeeder } = sim;
    const [mode, setMode] = useState('temperature');

    const list = buses(scope);
    const feeders = list.flatMap(bus => dc.buses[bus].feeders);
    const order = [...feeders].sort((a, b) => a.index - b.index || a.bus.localeCompare(b.bus));
    const hot = hottest(dc, scope);
    const coverage = thermalCoverage(dc, scope);
    const live = feeders.filter(f => !f.stale);
    const maxRise = Math.max(...live.map(f => f.rise));
    const measurable = feeders.filter(f => f.rUOhm !== null);
    const maxR = measurable.length ? measurable.reduce((top, f) => (f.rUOhm > top.rUOhm ? f : top)) : null;
    const toLimit = timeToCritical(hot);
    const A = dc.buses.A;
    const B = dc.buses.B;
    const conductorRise = (bus, i) => bus.conductor[i] - bus.ambient;

    return (
        <>
            <KpiRow>
                <Kpi label="Hottest monitored joint" value={fmt(hot.temp)} unit="°C" note={hot.joint} tag={tempTag(hot.temp)} />
                <Kpi label="Maximum temperature rise" value={fmt(maxRise)} unit="K" note="Above paired local ambient" />
                <Kpi label="Temperature channels" value={`${coverage.valid}/${coverage.total}`} note={coverage.stale ? `${coverage.stale} stale · excluded from aggregates` : 'All channels valid'} />
                <Kpi label="Highest joint resistance" value={maxR ? fmt(maxR.rUOhm) : '—'} unit={maxR ? 'µΩ' : ''} note={maxR ? `${maxR.joint} · derived, stable load above 50 A` : 'Needs a stable load above 50 A'} />
            </KpiRow>

            <Panel
                title="Joint temperature map"
                sub="Choose a joint to inspect its feeder and connection readings"
                actions={(
                    <Segmented
                        label="Map value"
                        value={mode}
                        onChange={setMode}
                        options={[{ value: 'temperature', label: 'Temperature' }, { value: 'rise', label: 'Rise above ambient' }]}
                    />
                )}
            >
                {list.map(bus => (
                    <div className="dash-map" key={bus}>
                        <h3><BusBadge bus={bus} /> Bus {bus} · joints and tap-offs</h3>
                        <ul>
                            {dc.buses[bus].feeders.map(feeder => {
                                const advisory = !feeder.stale && feeder.temp >= 60;
                                const critical = !feeder.stale && feeder.temp >= 75;

                                return (
                                    <li key={feeder.joint}>
                                        <button
                                            type="button"
                                            className={`dash-tile${advisory ? ' dash-tile--warn' : ''}${critical ? ' dash-tile--crit' : ''}${feeder.stale ? ' dash-tile--stale' : ''}`}
                                            onClick={() => openFeeder(feeder.id)}
                                        >
                                            <span>{feeder.joint}</span>
                                            <strong>
                                                {feeder.stale ? '—' : fmt(mode === 'rise' ? feeder.rise : feeder.temp)}
                                                {!feeder.stale && <small>{mode === 'rise' ? 'K rise' : '°C'}</small>}
                                            </strong>
                                            <em>{feeder.stale ? 'Unknown · stale' : critical ? 'Critical' : advisory ? 'Thermal warning' : 'Within limits'}</em>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                ))}
                <DiagramKey items={[
                    { mark: 'tile', label: 'Within limits' },
                    { mark: 'tile-warn', label: 'Thermal warning' },
                    { mark: 'tile-crit', label: 'Critical' },
                    { mark: 'tile-stale', label: 'Unknown / stale' }
                ]} />
                {inScope(scope, 'B') && <Foot>J06-B has no current temperature reading. Its electrical channels remain available, and the thermal channel is left out of hottest-point and rise calculations.</Foot>}
            </Panel>

            <Grid>
                <Panel title="Conductor & ambient" sub="Main bus · positive and negative conductors">
                    <BusTable scope={scope} rows={[
                        { label: 'Positive conductor', note: 'Main bus sensor · valid sample', a: fmt(A.conductor[0]), b: fmt(B.conductor[0]), unit: '°C' },
                        { label: 'Negative conductor', note: 'Main bus sensor · valid sample', a: fmt(A.conductor[1]), b: fmt(B.conductor[1]), unit: '°C' },
                        { label: 'Local ambient', note: 'Air sensor within each bus enclosure', a: fmt(A.ambient), b: fmt(B.ambient), unit: '°C' },
                        { label: 'Positive conductor rise', note: 'Conductor minus local ambient, derived', a: fmt(conductorRise(A, 0)), b: fmt(conductorRise(B, 0)), unit: 'K' },
                        { label: 'Negative conductor rise', note: 'Conductor minus local ambient, derived', a: fmt(conductorRise(A, 1)), b: fmt(conductorRise(B, 1)), unit: 'K' }
                    ]} />
                </Panel>

                <Panel title="Connection health" sub="Combine heat, load and contact voltage drop">
                    <Row label="Thermal channel coverage" note="12 joint + 4 conductor channels across both buses">{coverage.valid}/{coverage.total} in scope</Row>
                    <Row label="Online resistance estimate" note="Dedicated low-level joint voltage measurement / current"><code>R = ΔV / I</code></Row>
                    <Row label="Stable-load validity gate" note="|I| must exceed 50 A"><Pill>Derived</Pill></Row>
                    <Row label="Estimated resistive loss" note="Steady-state estimate for each monitored joint"><code>P = I²R</code></Row>
                    <Row label="Hottest-joint rise rate" note={`${hot.joint} · slope over the last 5 minutes`}>{hot.slope >= 0 ? '+' : ''}{fmt(hot.slope, 2)} K/min</Row>
                    <Row label="Time to 75 °C critical limit" note={`${hot.joint} · at the present rate of rise, if nothing changes`}>
                        {hot.temp >= 75 ? 'Over the limit' : toLimit === null ? 'Not heading there' : toLimit >= 60 ? `About ${fmt(toLimit / 60)} h` : `About ${fmt(toLimit, 0)} min`}
                    </Row>
                    <Foot>Live contact resistance is an estimate with sensor uncertainty and load qualification, not a de-energized four-wire micro-ohmmeter test. Temperature limits need the actual busway and connection specifications.</Foot>
                </Panel>
            </Grid>

            <Panel title="Joint diagnostics" sub="Voltage drop is measured directly across each monitored connection">
                <Table>
                    <thead>
                        <tr>
                            <th scope="col">Joint</th>
                            <th scope="col" className="is-num">Load current</th>
                            <th scope="col" className="is-num">Temperature</th>
                            <th scope="col" className="is-num">Rise</th>
                            <th scope="col" className="is-num">5-min rise rate</th>
                            <th scope="col" className="is-num">Joint drop</th>
                            <th scope="col" className="is-num">R estimate</th>
                            <th scope="col" className="is-num">I²R loss</th>
                            <th scope="col">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {order.map(feeder => {
                            const condition = dc.conditions.find(c => c.feeder === feeder.id && c.kind === 'thermal');

                            return (
                                <tr key={feeder.joint} className={condition || feeder.stale ? 'is-flagged' : undefined}>
                                    <th scope="row">
                                        <button type="button" className="dash-tablelink" onClick={() => openFeeder(feeder.id)}>
                                            <BusBadge bus={feeder.bus} /> {feeder.joint}
                                        </button>
                                        <span className="dash-table__inline">Section {String(feeder.index + 1).padStart(2, '0')}</span>
                                    </th>
                                    <td className="is-num">{fmt(feeder.amps)} A</td>
                                    <td className="is-num">{feeder.stale ? '—' : `${fmt(feeder.temp)} °C`}</td>
                                    <td className="is-num">{feeder.stale ? '—' : `${fmt(feeder.rise)} K`}</td>
                                    <td className="is-num">{feeder.stale ? '—' : `${feeder.slope >= 0 ? '+' : ''}${fmt(feeder.slope, 2)} K/min`}</td>
                                    <td className="is-num">{fmt(feeder.dropMv, 2)} mV</td>
                                    <td className="is-num">{feeder.rUOhm === null ? '—' : `${fmt(feeder.rUOhm)} µΩ`}</td>
                                    <td className="is-num">{fmt(feeder.lossW, 2)} W</td>
                                    <td><StatusFor feeder={feeder} condition={condition} /></td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
                <Caption>{plural(order.length, 'connection')} in scope · Rate is the 5-minute temperature slope · R and loss are derived</Caption>
            </Panel>
        </>
    );
}

/* ── Protection & alarms ────────────────────────────────────── */

const deviceStatus = (anyOpen, mainsClosed) => [
    ['Main & feeder trip units', null, <Pill tone="ok" key="a">Healthy</Pill>],
    ['Last protection trip', null, anyOpen ? <Pill tone="crit" key="b">Bus main open</Pill> : <Pill key="b">None in history</Pill>],
    ['Main contactors', null, <Pill tone={mainsClosed ? 'ok' : 'crit'} key="c">{mainsClosed ? 'Closed' : 'Open'}</Pill>],
    ['Precharge sequence', null, <Pill tone="ok" key="d">Complete</Pill>],
    ['Surge protection contacts', null, <Pill tone="ok" key="e">Healthy</Pill>],
    ['DC arc-fault detection', null, <Pill key="f">Not fitted</Pill>],
    ['Breaker interrupting rating', 'DC voltage and test conditions required', <Pill key="g">Not configured</Pill>],
    ['Busbar short-circuit withstand', 'Nameplate or study input, not a measured current', <Pill key="h">Not configured</Pill>]
];

export function Protection({ sim }) {
    const { dc, scope, anchor, acks, acknowledge, openFeeder, setView } = sim;

    const conditions = scopedConditions(dc, scope);
    const open = conditions.filter(c => !acks[c.id]).length;
    const list = buses(scope);
    const A = dc.buses.A;
    const B = dc.buses.B;

    const breakers = list.length * 7;
    const feederCount = list.length * 6;
    const mains = list.length;
    const closed = list.reduce((total, bus) => total + (dc.buses[bus].mainClosed ? 1 : 0) + dc.buses[bus].feeders.filter(f => f.breaker === 'Closed').length, 0);
    const critical = conditions.filter(c => c.severity === 'critical').length;
    const anyOpen = closed < breakers;
    const mainsClosed = list.every(bus => dc.buses[bus].mainClosed);

    const assetBus = asset => (/^J\d+-([AB])$/.exec(asset) || [])[1] || (/^Bus ([AB])$/.exec(asset) || [])[1] || null;

    const rows = [
        ...protectionHistory(anchor),
        ...sim.alarmLog,
        ...Object.entries(acks).map(([asset, at]) => ({
            at,
            asset,
            event: 'Advisory acknowledged in this session',
            state: 'Acknowledged'
        }))
    ]
        .filter(row => {
            const bus = assetBus(row.asset);
            return !bus || inScope(scope, bus);
        })
        .sort((a, b) => b.at - a.at);

    return (
        <>
            <KpiRow>
                <Kpi label="Critical alarms" value={critical} note={critical ? 'Needs attention now' : 'No critical condition'} tag={critical ? <Pill tone="crit">Act</Pill> : null} />
                <Kpi label="Active advisories" value={conditions.length} note={`${open} unacknowledged`} />
                <Kpi label="Breakers closed" value={`${closed}/${breakers}`} note={`${plural(mains, 'main')} + ${feederCount} feeders in scope`} />
                <Kpi label="Lowest insulation" value={fmt(Math.min(...list.map(b => dc.buses[b].insulation)), 2)} unit="MΩ" note="IMD self-test passed" tag={<Pill tone="ok">Valid</Pill>} />
            </KpiRow>

            <Panel title="Active conditions" sub="Acknowledging records awareness. The condition stays active.">
                {conditions.length === 0 && <Note>No active conditions on this bus.</Note>}

                <ul className="dash-conditions">
                    {conditions.map(condition => (
                        <li key={condition.id}>
                            <div>
                                <h3>{condition.title}</h3>
                                <p>{condition.body}</p>
                                <p className="dash-conditions__meta">
                                    <SeverityPill severity={condition.severity} />
                                    <span>{condition.where} · {clock(condition.at)} ET</span>
                                </p>
                            </div>

                            <div className="dash-conditions__actions">
                                {acks[condition.id] ? (
                                    <Pill tone="info">Acknowledged locally · still active</Pill>
                                ) : (
                                    <button type="button" className="dash-btn" onClick={() => acknowledge(condition.id)}>Acknowledge</button>
                                )}
                                {condition.feeder && <button type="button" className="dash-link" onClick={() => openFeeder(condition.feeder)}>Inspect feeder →</button>}
                            </div>
                        </li>
                    ))}
                </ul>
            </Panel>

            <Grid>
                <Panel title="Insulation & earth monitoring" sub="Floating two-wire system · separate monitors per isolated bus">
                    <BusTable scope={scope} rows={[
                        { label: 'System insulation resistance', note: 'Insulation monitor; valid measurement state', a: fmt(A.insulation, 2), b: fmt(B.insulation, 2), unit: 'MΩ' },
                        { label: 'Residual current · AC+DC RMS', note: 'DC-capable zero-flux sensor · DC–1 kHz band', a: fmt(A.residual), b: fmt(B.residual), unit: 'mA' },
                        { label: 'System leakage capacitance', note: 'IMD estimate to protective earth', a: fmt(A.capacitance), b: fmt(B.capacitance), unit: 'µF' },
                        { label: 'IMD self-test / connection', note: 'Simulated instrument diagnostics', a: <Pill tone="ok">Passed</Pill>, b: <Pill tone="ok">Passed</Pill>, unit: 'status' },
                        { label: 'Branch fault locator', note: 'No branch-level insulation localization', a: <Pill>Not fitted</Pill>, b: <Pill>Not fitted</Pill>, unit: 'status' }
                    ]} />
                    <Foot>Pole-to-earth symmetry does not prove good insulation. If A and B became galvanically connected, the insulation monitors would have to be coordinated.</Foot>
                </Panel>

                <Panel title="Protective device status" sub="Reported state and configured ratings">
                    {deviceStatus(anyOpen, mainsClosed).map(([label, note, value]) => (
                        <Row label={label} note={note} key={label}>{value}</Row>
                    ))}
                </Panel>
            </Grid>

            <Panel
                title="Monitoring thresholds"
                sub="Monitoring limits, not protective relay or breaker settings"
                badge={<Pill>Alarm engine</Pill>}
                actions={<button type="button" className="dash-link" onClick={() => setView('library')}>Definitions →</button>}
            >
                <ul className="limits dash-limits">
                    {THRESHOLDS.map(limit => (
                        <li className="limits__card" key={limit.label}>
                            <span className="limits__label">{limit.label}</span>
                            <span className="limits__value">{limit.value}</span>
                            <span className="limits__note">{limit.note}</span>
                        </li>
                    ))}
                </ul>
                <Foot>The alarms on this page follow these limits, delays and reset points. They are starting points that need equipment-specific review. Independent protection performs any trip action, and this interface has no switching or trip controls.</Foot>
            </Panel>

            <Panel title="Event history" sub="Eastern time">
                <Table>
                    <thead>
                        <tr>
                            <th scope="col">Time</th>
                            <th scope="col">Asset</th>
                            <th scope="col">Event</th>
                            <th scope="col">State</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(row => (
                            <tr key={`${row.asset}-${row.at}-${row.event}-${row.state}`}>
                                <th scope="row">{clock(row.at)} ET</th>
                                <td>{row.asset}</td>
                                <td>{row.event}</td>
                                <td>{row.state}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Panel>
        </>
    );
}

/* ── Energy & backup ────────────────────────────────────────── */

function Rectifier({ bus, data }) {
    return (
        <Panel
            title={`Rectifier ${bus}`}
            sub="9 × 400 kW modules · 3.6 MW installed per path"
            badge={<Pill tone={data.mode === 'normal' && data.rectifier.online === 9 ? 'ok' : 'crit'}>{data.mode === 'normal' ? `${data.rectifier.online}/9 online` : data.rectifier.state}</Pill>}
        >
            <ul className="dash-modules" aria-label={`Rectifier ${bus} modules, ${data.rectifier.online} of ${SYSTEM.modules} online`}>
                {Array.from({ length: SYSTEM.modules }, (_, i) => {
                    const name = `M${String(i + 1).padStart(2, '0')}`;
                    const failed = data.rectifier.failedModule === name;
                    const idle = data.mode !== 'normal';

                    return (
                        <li className={failed ? 'is-failed' : idle ? 'is-idle' : undefined} key={name}>
                            <i aria-hidden="true" />{name}{failed && <span className="dash-sr"> failed</span>}
                        </li>
                    );
                })}
            </ul>
            <Readout items={[
                { label: 'DC output', value: fmt(data.rectifier.outputKw), unit: 'kW' },
                { label: 'Conversion efficiency', value: fmt(data.rectifier.efficiency), unit: '%' },
                { label: 'Path operating limit', value: fmt(data.rectifier.limitKw / 1000, 3), unit: 'MW' }
            ]} />
            <Row label="Module redundancy" note="Capacity with one 400 kW module unavailable"><Pill tone="info">{fmt(data.rectifier.nPlusOneKw / 1000)} MW N+1</Pill></Row>
            <Row label="Current sharing" note="Module telemetry at present load">{fmt(data.rectifier.sharing)}% maximum deviation</Row>
            <Row label="Bus continuous limit" note="Configured operating limit">4,000 A</Row>
            <Foot>Path capacity is the lower of available rectifier capacity and bus voltage × the approved current limit. Module redundancy and A/B path redundancy are evaluated separately.</Foot>
        </Panel>
    );
}

function Battery({ bus, data, totalKw }) {
    const { battery } = data;

    return (
        <Panel title={`Battery ${bus}`} sub="Independent storage through a DC/DC interface" badge={<Pill tone={battery.state === 'Discharging' ? 'crit' : 'ok'}>{battery.state} · {fmt(battery.powerKw, 0)} kW</Pill>}>
            <div className="dash-soc">
                <span className="dash-soc__cell" aria-hidden="true"><i style={{ width: `${battery.soc}%` }} /></span>
                <strong>{fmt(battery.soc, 0)}<small>%</small></strong>
                <span>State of charge · BMS estimate</span>
            </div>
            <Readout items={[
                { label: 'State of health', value: fmt(battery.soh), unit: '%' },
                { label: 'Usable at bus', value: fmt(battery.usableKwh), unit: 'kWh' },
                { label: 'Discharge limit', value: fmt(battery.limitMw), unit: 'MW' }
            ]} />
            <Row label="Runtime at present path load" note={`${fmt(data.kw)} kW on Bus ${bus}`}>{fmt(battery.runtimeNow)} min</Row>
            <Row label="Runtime after loss of the other path" note={`${fmt(totalKw)} kW total protected load`}>{fmt((battery.usableKwh / totalKw) * 60)} min</Row>
            <Row label="Cell voltage range" note={`Spread: ${Math.round((battery.cells[1] - battery.cells[0]) * 1000)} mV`}>{fmt(battery.cells[0], 3)}–{fmt(battery.cells[1], 3)} V</Row>
            <Row label="Battery temperature range" note="Simulated BMS minimum and maximum">{fmt(battery.temps[0])}–{fmt(battery.temps[1])} °C</Row>
            <Foot>Runtime is an estimate: usable bus-side energy divided by supported load. The 10% reserve and conversion allowance are already in the usable figure. BMS limits, temperature and actual load govern available backup.</Foot>
        </Panel>
    );
}

export function Energy({ sim }) {
    const { dc, scope, now } = sim;
    const list = buses(scope);
    const A = dc.buses.A;
    const B = dc.buses.B;

    const sample = Math.floor(now / 300000);
    const bars = useMemo(() => {
        const series = list.map(bus => ({ bus, hours: hourlyEnergy(bus, now) }));

        return series[0].hours.map((hour, i) => ({
            key: hour.t,
            label: `${clock(hour.t)} ET`,
            t: hour.t,
            i,
            values: series.map(s => ({
                id: s.bus,
                label: `Bus ${s.bus}`,
                color: BUS_COLOR[s.bus],
                value: s.hours[i].kwh
            }))
        }));
    }, [scope, sample]); // eslint-disable-line react-hooks/exhaustive-deps

    const importKwh = list.reduce((t, bus) => t + dc.buses[bus].importKwh, 0);
    const demand = list.reduce((t, bus) => t + dc.buses[bus].demandKw, 0);

    return (
        <>
            <KpiRow>
                <Kpi label="Imported energy · 24 h" value={fmt(importKwh / 1000, 2)} unit="MWh" note="Separate import register" />
                <Kpi label="Exported energy · 24 h" value="0.0" unit="kWh" note="No reverse flow" />
                <Kpi label="Net energy · 24 h" value={fmt(importKwh / 1000, 2)} unit="MWh" note="Import minus export" />
                <Kpi label="15-minute rolling demand" value={fmt(demand / 1000, 3)} unit="MW" note="Updated at 5-minute sample boundaries" />
            </KpiRow>

            <Grid>
                <Panel title="24-hour energy profile" sub="One-hour imported-energy windows · simulated historian">
                    {list.length > 1
                        ? <Legend mark="bar" items={[{ label: 'Bus A', color: BUS_COLOR.A }, { label: 'Bus B', color: BUS_COLOR.B }]} />
                        : <p className="dash-axis">Bus {scope}</p>}
                    <GroupedBars
                        groups={bars}
                        height={280}
                        label="Hourly imported energy over the last 24 hours. The registers below give the totals."
                        formatY={value => `${value}`}
                        formatTip={value => `${fmt(value)} kWh`}
                        tipTitle={group => `Hour ending ${group.label}`}
                        tickLabel={group => clock(group.t)}
                        showTick={group => group.i % 6 === 5}
                    />
                    <p className="dash-axis">kWh per hour · Eastern time</p>
                    <Foot>Bars integrate the 5-minute samples. Totals always show the trailing 24 hours, and the bus selector applies.</Foot>
                </Panel>

                <Panel title="Capacity after one path is lost" sub="Whole-system contingency · independent of the selected bus" badge={<Pill tone="info">A/B model</Pill>}>
                    <Readout items={[
                        { label: 'Surviving path capacity', value: fmt(dc.capacityKw / 1000, 3), unit: 'MW', note: 'At the lower measured bus voltage' },
                        { label: 'Remaining power headroom', value: fmt(dc.headroomKw / 1000, 2), unit: 'MW', note: 'After supporting all present load' }
                    ]} />
                    <div className="dash-meter">
                        <span className="dash-meter__head">
                            <span>Surviving bus utilization</span>
                            <strong>{fmt(dc.survivingUse)}%</strong>
                        </span>
                        <span className="dash-meter__track"><i style={{ width: `${dc.survivingUse}%` }} /></span>
                        <span className="dash-meter__foot">
                            <span>{fmt(dc.totalKw / 1000, 2)} MW protected load</span>
                            <span>{fmt(dc.capacityKw / 1000, 3)} MW limit</span>
                        </span>
                    </div>
                    <Row label="Highest feeder utilization after path loss" note="Paired A/B feed transferred to the surviving branch">{fmt(Math.max(...dc.pairLoading))}%</Row>
                    <Row label="Installed nominal bus capacity" note="2 × 800 V × 4,000 A">6.4 MW total</Row>
                    <Row label="Nominal redundant capacity" note="One complete surviving path">3.2 MW</Row>
                    <Foot>This assumes the protected AC loads can be supported by either UPS path and its inverter sections. Source, feeder, converter and load-input limits must all permit that load. Installed A+B capacity is not redundant capacity.</Foot>
                </Panel>
            </Grid>

            <Panel title="Energy & demand registers" sub="Metered quantities, derived intervals and source efficiency">
                <BusTable scope={scope} rows={[
                    { label: 'Imported energy · trailing 24 h', note: 'Separate positive-energy register', a: fmt(A.importKwh), b: fmt(B.importKwh), unit: 'kWh' },
                    { label: 'Exported energy · trailing 24 h', note: 'Separate negative-energy register', a: '0.0', b: '0.0', unit: 'kWh' },
                    { label: 'Net energy · trailing 24 h', note: 'Import minus export', a: fmt(A.importKwh), b: fmt(B.importKwh), unit: 'kWh' },
                    { label: '15-minute rolling demand', note: 'Rolling average at 5-minute sample boundaries', a: fmt(A.demandKw), b: fmt(B.demandKw), unit: 'kW' },
                    { label: 'Maximum 15-minute rolling demand', note: 'Per-path maximum in 24 h; independent maxima are not added', a: fmt(A.peakDemandKw), b: fmt(B.peakDemandKw), unit: 'kW' },
                    { label: 'Rectifier conversion efficiency', note: 'Time-aligned DC output / AC input active power', a: fmt(A.rectifier.efficiency), b: fmt(B.rectifier.efficiency), unit: '%' }
                ]} />
                <Foot>Positive power is source-to-load flow, and export registers accumulate negative active power separately.</Foot>
            </Panel>

            <h2 className="dash-section">Rectifier availability</h2>
            <Grid cols={list.length > 1 ? '1fr 1fr' : '1fr'}>
                {list.map(bus => <Rectifier key={bus} bus={bus} data={dc.buses[bus]} />)}
            </Grid>

            <h2 className="dash-section">Battery storage & ride-through</h2>
            <Grid cols={list.length > 1 ? '1fr 1fr' : '1fr'}>
                {list.map(bus => <Battery key={bus} bus={bus} data={dc.buses[bus]} totalKw={dc.totalKw} />)}
            </Grid>

        </>
    );
}
